import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Routes, Route, Link, NavLink, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StatsProvider, MediaMagicProvider, DevProvider } from '@media-magic/sdk';
import { initGA, trackPageView } from './config/analytics';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { demoApi } from './services/demoApi';
import { Gallery } from './pages/Gallery';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Analytics } from './pages/Analytics';
import { Documentation } from './pages/Documentation';
import { ApiReference } from './pages/ApiReference';
import { SDKDevDemo } from './pages/SDKDevDemo';
import { APIKeys } from './pages/APIKeys';
import { Home } from './pages/Home';
import { Dashboard } from './pages/Dashboard';
import { TryNow } from './pages/TryNow';
import { ForgotPassword } from './pages/ForgotPassword';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';

const DEMO_FALLBACK_CLIENT_ID = import.meta.env.VITE_MM_CLIENT_ID || 'public-demo';
const SDK_CONFIG = {
  clientId: import.meta.env.VITE_MM_CLIENT_ID || '',
  cdnUrl: import.meta.env.VITE_MM_CDN_URL || '',
  assets: ['assets', 'test-assets'],
};

function SdkProvider({ children }: { children: ReactNode }) {
  if (import.meta.env.DEV) {
    return <DevProvider config={{ sdkConfig: SDK_CONFIG }}>{children}</DevProvider>;
  }
  return <MediaMagicProvider config={SDK_CONFIG}>{children}</MediaMagicProvider>;
}

function AppContent() {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const isDemoMobilePreview =
    location.pathname === '/demo' &&
    new URLSearchParams(location.search).get('mobilePreview') === '1';
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link${isActive ? ' active' : ''}`;

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setAccountMenuOpen(false);
  }, [location.pathname, location.search]);

  // Migrate guest demos to authenticated user on login
  useEffect(() => {
    if (!user?.userId) return;
    void demoApi.migrateGuestDemos(user.userId).catch(() => {}); // silent
  }, [user?.userId]);

  useEffect(() => {
    if (!accountMenuOpen) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [accountMenuOpen]);

  return (
    <div className="app-shell">
      {!isDemoMobilePreview && (
        <>
          <div className="shell-glow shell-glow--one" aria-hidden="true" />
          <div className="shell-glow shell-glow--two" aria-hidden="true" />
        </>
      )}
      {!isDemoMobilePreview && (
        <header className="top-nav-wrap">
          <nav className="top-nav">
            <div className="top-nav__left">
              <Link to="/" className="brand-link" aria-label="Media Magic home">
                <span className="brand-mark" aria-hidden="true">MM</span>
                <span>
                  <span className="brand-title">Media Magic</span>
                  <span className="brand-subtitle">Adaptive Media Platform</span>
                </span>
              </Link>

              <div className="nav-links" aria-label="Primary navigation">
                <NavLink to="/" className={navLinkClass} end>
                  Home
                </NavLink>
                <NavLink to="/demo" className={navLinkClass}>
                  Demo
                </NavLink>
                <NavLink to="/try-now" className={navLinkClass}>
                  Quick Check
                </NavLink>
                {user && (
                  <>
                    <NavLink to="/analytics" className={navLinkClass}>
                      Insights
                    </NavLink>
                    <NavLink to="/gallery" className={navLinkClass}>
                      Library
                    </NavLink>
                  </>
                )}
                <NavLink to="/docs" className={navLinkClass} end>
                  Documentation
                </NavLink>
                <NavLink to="/docs/reference" className={navLinkClass}>
                  API Reference
                </NavLink>
              </div>
            </div>

            <div className="top-nav__right">
              {loading ? (
                <span className="user-note">Checking session...</span>
              ) : user ? (
                <div className="account-menu" ref={accountMenuRef}>
                  <button
                    className="btn btn-ghost account-menu__toggle"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={accountMenuOpen}
                    onClick={() => setAccountMenuOpen((prev) => !prev)}
                  >
                    Account
                  </button>
                  {accountMenuOpen && (
                    <div className="account-menu__panel" role="menu">
                      <div className="account-menu__identity">{user.email || user.username}</div>
                      <Link
                        to="/api-keys"
                        className="account-menu__item"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        API Keys
                      </Link>
                      <button
                        className="account-menu__item account-menu__item--danger"
                        type="button"
                        role="menuitem"
                        onClick={async () => {
                          setAccountMenuOpen(false);
                          await logout();
                        }}
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="nav-inline-link">
                    Sign In
                  </Link>
                  <Link to="/signup" className="btn btn-solid">
                    Start Free
                  </Link>
                </>
              )}
            </div>
          </nav>
        </header>
      )}

      <main className={isDemoMobilePreview ? 'app-main app-main--demo' : 'app-main'}>
        <Routes>
          <Route path="/" element={user ? <ProtectedRoute><DashboardPage /></ProtectedRoute> : <Home />} />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/docs/reference" element={<ApiReference />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/try-now" element={<TryNowPage />} />
          <Route path="/try-now/results" element={<TryNowResultsPage />} />
          <Route path="/preview-results" element={<PreviewResultsPage />} />
          <Route
            path="/gallery"
            element={
              <ProtectedRoute>
                <GalleryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <APIKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/demo"
            element={
              <SDKDevDemoPage />
            }
          />
        </Routes>
      </main>
      {!isDemoMobilePreview && (
        <footer className="app-footer">
          <div className="app-footer__inner">
            <div className="app-footer__links">
              <Link to="/privacy" className="app-footer__link">Privacy Policy</Link>
              <span className="app-footer__dot">•</span>
              <Link to="/terms" className="app-footer__link">Terms of Service</Link>
            </div>
            <p className="app-footer__copy">© 2026 Media Magic. All rights reserved.</p>
          </div>
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SdkProvider>
        <StatsProvider>
          <AppContent />
        </StatsProvider>
      </SdkProvider>
    </AuthProvider>
  );
}

function GalleryPage() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return <Gallery userId={user.userId} />;
}

function AnalyticsPage() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return <Analytics userId={user.userId} />;
}

function APIKeysPage() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return <APIKeys userId={user.userId} />;
}

function DashboardPage() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return <Dashboard userId={user.userId} />;
}

function SDKDevDemoPage() {
  const { user } = useAuth();
  return <SDKDevDemo userId={user?.userId || DEMO_FALLBACK_CLIENT_ID} />;
}

function TryNowPage() {
  const { user } = useAuth();

  return <TryNow userId={user?.userId} />;
}

function TryNowResultsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const shareGroupId = new URLSearchParams(location.search).get('group');

  if (!shareGroupId) {
    return <Navigate to="/try-now" replace />;
  }

  return <TryNow userId={user?.userId} />;
}

function PreviewResultsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const shareGroupId = new URLSearchParams(location.search).get('group');

  if (!shareGroupId) {
    return <Navigate to="/login" replace />;
  }

  return <TryNow userId={user?.userId} />;
}

export default App;
